/**
 * Runtime type-guards for wire payloads. Peers deserialize untrusted
 * data from the network; every consumer at the wire boundary runs
 * these before treating an object as a typed payload.
 *
 * The guards are strict on REQUIRED fields and tolerant on UNKNOWN
 * fields — a future protocol bump (v=2) that adds new fields stays
 * compatible because the v=1 guard only checks what v=1 needed.
 */
import { MESH_PROTOCOL_VERSION, } from './types.js';
function isRecord(x) {
    return typeof x === 'object' && x !== null && !Array.isArray(x);
}
function isStringArr(x) {
    return Array.isArray(x) && x.every((s) => typeof s === 'string');
}
export function isMeshToolDescriptor(x) {
    if (!isRecord(x))
        return false;
    return (typeof x.name === 'string' &&
        typeof x.description === 'string' &&
        isRecord(x.inputSchema));
}
export function isMeshPeerCap(x) {
    if (!isRecord(x))
        return false;
    if (x.v !== MESH_PROTOCOL_VERSION)
        return false;
    if (typeof x.ts !== 'number')
        return false;
    if (typeof x.nick !== 'string')
        return false;
    if (typeof x.modelId !== 'string')
        return false;
    if (typeof x.available !== 'boolean')
        return false;
    if (!isStringArr(x.skills))
        return false;
    if (typeof x.systemPromptSummary !== 'string')
        return false;
    if (!Array.isArray(x.tools))
        return false;
    if (!x.tools.every(isMeshToolDescriptor))
        return false;
    // Layer-4 fields are optional but, when present, MUST be string[].
    // Missing field = empty array semantically (handled in resolver).
    if (x.authoritative !== undefined && !isStringArr(x.authoritative))
        return false;
    if (x.delegating !== undefined && !isStringArr(x.delegating))
        return false;
    // Phase C: stageHost is optional but, when present, MUST be well-formed —
    // a malformed stageHost block shouldn't sink the whole cap (v1 additive
    // idiom), so we reject only the cap if stageHost itself is garbage,
    // not merely absent.
    if (x.stageHost !== undefined && !isStageHostCap(x.stageHost))
        return false;
    return true;
}
/**
 * Runtime guard for the `MeshPeerCap.stageHost` block (Phase C). Exported
 * separately so callers that only care about stage-hosting capability
 * (e.g. `stagePlanner.ts` filtering a roster snapshot) can validate a
 * loosely-typed value without going through the full cap guard.
 */
export function isStageHostCap(x) {
    if (!isRecord(x))
        return false;
    if (x.vramBytes !== undefined && typeof x.vramBytes !== 'number')
        return false;
    if (typeof x.maxStorageBufferBytes !== 'number' || x.maxStorageBufferBytes <= 0)
        return false;
    if (typeof x.wasmHeapBudget !== 'number' || x.wasmHeapBudget <= 0)
        return false;
    if (x.cachedFragments !== undefined && !isStringArr(x.cachedFragments))
        return false;
    if (x.stability !== undefined) {
        const s = x.stability;
        if (!isRecord(s))
            return false;
        if (typeof s.keepalive !== 'boolean')
            return false;
        if (typeof s.visible !== 'boolean')
            return false;
        if (s.pinned !== undefined && typeof s.pinned !== 'boolean')
            return false;
        if (s.onBattery !== undefined && typeof s.onBattery !== 'boolean')
            return false;
        if (typeof s.uptimeMs !== 'number' || s.uptimeMs < 0)
            return false;
    }
    if (x.maxSessions !== undefined && (typeof x.maxSessions !== 'number' || x.maxSessions <= 0))
        return false;
    if (x.activeSessions !== undefined && (typeof x.activeSessions !== 'number' || x.activeSessions < 0))
        return false;
    if (x.loadedStages !== undefined) {
        if (!Array.isArray(x.loadedStages))
            return false;
        if (!x.loadedStages.every(isMeshLoadedStage))
            return false;
    }
    return true;
}
/** Runtime guard for one `MeshPeerCap.stageHost.loadedStages[]` entry (M3). */
export function isMeshLoadedStage(x) {
    if (!isRecord(x))
        return false;
    if (typeof x.modelId !== 'string' || !x.modelId)
        return false;
    if (!Number.isInteger(x.layerStart) || x.layerStart < 0)
        return false;
    if (!Number.isInteger(x.layerEnd) || x.layerEnd <= x.layerStart)
        return false;
    if (typeof x.includeEmbeddings !== 'boolean')
        return false;
    if (typeof x.includeOutput !== 'boolean')
        return false;
    if (!Number.isInteger(x.ctxSize) || x.ctxSize <= 0)
        return false;
    // wireDtype is OPTIONAL (additive-idiom): a peer on a bundle predating the
    // f16-wire field omits it, and rejecting the whole cap over a missing
    // descriptor would silently drop a capable stage host on any version skew.
    // Absent => f16 default at ingestion (collectCommunalAds).
    if (x.wireDtype !== undefined && x.wireDtype !== 'f32' && x.wireDtype !== 'f16' && x.wireDtype !== 'i8')
        return false;
    if (!Number.isInteger(x.maxSessions) || x.maxSessions <= 0)
        return false;
    if (!Number.isInteger(x.activeSessions) || x.activeSessions < 0)
        return false;
    if (!Number.isInteger(x.epoch) || x.epoch < 0)
        return false;
    return true;
}
export function isMeshChatMessage(x) {
    if (!isRecord(x))
        return false;
    if (x.v !== MESH_PROTOCOL_VERSION)
        return false;
    if (typeof x.ts !== 'number')
        return false;
    if (typeof x.from !== 'string')
        return false;
    if (typeof x.to !== 'string')
        return false;
    if (x.bodyKind !== 'text' && x.bodyKind !== 'tokens' && x.bodyKind !== 'frame') {
        return false;
    }
    if (x.bodyKind === 'text' && typeof x.text !== 'string')
        return false;
    if (x.bodyKind === 'tokens') {
        if (!Array.isArray(x.ids))
            return false;
        if (!x.ids.every((n) => typeof n === 'number' && Number.isInteger(n) && n >= 0)) {
            return false;
        }
    }
    if (x.bodyKind === 'frame' && typeof x.frame !== 'string')
        return false;
    if (x.mapId !== undefined && typeof x.mapId !== 'string')
        return false;
    if (x.safety !== undefined && !isRecord(x.safety))
        return false;
    return true;
}
export function isMeshToolCall(x) {
    if (!isRecord(x))
        return false;
    if (x.v !== MESH_PROTOCOL_VERSION)
        return false;
    return (typeof x.ts === 'number' &&
        typeof x.callId === 'string' &&
        typeof x.toolName === 'string' &&
        isRecord(x.args));
}
export function isMeshToolResult(x) {
    if (!isRecord(x))
        return false;
    if (x.v !== MESH_PROTOCOL_VERSION)
        return false;
    if (typeof x.ts !== 'number')
        return false;
    if (typeof x.callId !== 'string')
        return false;
    return x.status === 'ok' || x.status === 'error' || x.status === 'denied';
}
export function isMeshToolFrame(x) {
    if (!isRecord(x))
        return false;
    if (x.kind === 'call')
        return isMeshToolCall(x);
    if (x.kind === 'result')
        return isMeshToolResult(x);
    return false;
}
